import { useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  ConfigProvider,
  Card,
  Space,
  Tag,
  Tooltip,
  Input,
  Empty,
  Grid,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  SyncOutlined,
  SwapOutlined,
  SearchOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { motion } from "framer-motion";
import { useModalForm } from "../../hooks/useModalForm";
import FamilyForm from "./components/FamilyForm/FamilyForm";
import { useDeleteFamily, useGetFamilies } from "network/queries/family.query";
import {
  IChildInfo,
  IFamily,
  IFamilyResponse,
} from "network/models/family.model";

const { Search } = Input;

// === Helpers ===
const getFullName = (p?: {
  firstName?: string;
  middleName?: string;
  lastName?: string;
}) =>
  [p?.firstName, p?.middleName, p?.lastName].filter(Boolean).join(" ") || "-";

const fmtDate = (d?: string | Date) => {
  if (!d) return "";
  try {
    const date = typeof d === "string" ? new Date(d) : d;
    return !isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : "";
  } catch {
    return "";
  }
};

export default function FamiliesList() {
  const screens = Grid.useBreakpoint?.() || {};
  const isMobile = !screens.md;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [query, setQuery] = useState<string>();
  const [rtl, setRtl] = useState(true);
  const navigate = useNavigate();

  const {
    data: { data } = { data: undefined as any },
    isLoading,
    refetch,
    isRefetching,
  } = useGetFamilies({ page: page, limit: pageSize });
  const { mutate: deleteFamily } = useDeleteFamily();

  const {
    items: families = [],
    total,
    page: apiPage,
    limit,
  } = (data || {}) as IFamilyResponse;
  const { modalForm, add, edit, reset } = useModalForm<IFamily>();

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const remove = async (id: string) =>
    deleteFamily(id, { onSuccess: () => refetch() });

  const filtered: IFamily[] = useMemo(() => {
    if (!query) return families;
    const lower = query.toLowerCase();
    return families.filter((f) => {
      const fatherName = getFullName(f.father).toLowerCase();
      const motherName = getFullName(f.mother).toLowerCase();
      const book = f.father?.familyBookNumber?.toString() ?? "";
      return (
        fatherName.includes(lower) ||
        motherName.includes(lower) ||
        book.includes(lower)
      );
    });
  }, [query, families]);

  // === Export to Excel ===
  const exportExcel = () => {
    const rows: Record<string, any>[] = families.map((f, i) => {
      const father = getFullName(f.father);
      const mother = getFullName(f.mother);
      const row: Record<string, any> = {
        التسلسل: i + 1,
        "رقم الدفتر": f.father?.familyBookNumber ?? "",
        "اسم الأب": father,
        "الرقم الوطني (أب)": f.father?.nationalId ?? "",
        "الميلاد (أب)": fmtDate(f.father?.birthdate),
        "الحالة الصحية (أب)": f.father?.healthStatus ?? "",
        "العمل (أب)": f.father?.professional ?? "",
        "الهاتف (أب)": f.father?.phone ?? "",
        "اسم الأم": mother,
        "الرقم الوطني (أم)": f.mother?.nationalId ?? "",
        "الميلاد (أم)": fmtDate(f.mother?.birthdate),
        "الحالة الصحية (أم)": f.mother?.healthStatus ?? "",
        "العمل (أم)": f.mother?.professional ?? "",
        "الهاتف (أم)": f.mother?.phone ?? "",
      };
      const labels = ["الأول", "الثاني", "الثالث"];
      labels.forEach((label, i) => {
        const child: IChildInfo | undefined = f.children?.[i];
        row[`الابن ${label} - الاسم`] = child ? getFullName(child) : "";
        row[`الابن ${label} - الميلاد`] = child ? fmtDate(child.birthdate) : "";
        row[`الابن ${label} - الوضع الدراسي`] = child?.education ?? "";
        row[`الابن ${label} - الحالة الصحية`] = child?.healthStatus ?? "";
      });
      return row;
    });
    // @ts-ignore
    const worksheet = XLSX.utils.json_to_sheet(rows, { origin: "A2" });
    worksheet["rtl"] = true;
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "سجل العائلات");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer]), "family-register.xlsx");
  };

  const columns: ColumnsType<IFamily> = [
    {
      title: <span className="font-bold">م</span>,
      align: "center",
      render: (_: any, __: IFamily, index: number) => (
        <Tag color="blue" className="font-bold px-2 py-1">
          {index + 1}
        </Tag>
      ),
      responsive: ["sm"],
    },
    {
      title: "رقم دفتر العائلة",
      render: (r: IFamily) => (
        <Tag
          color="blue"
          className="cursor-pointer text-base font-bold px-3 py-1"
          onClick={() => navigate(`/families/${r._id}/father`)}
        >
          {r.father?.familyBookNumber ?? "-"}
        </Tag>
      ),
    },
    {
      title: "👨 الأب",
      render: (r: IFamily) => (
        <span
          onClick={() => navigate(`/families/${r._id}/father`)}
          className="cursor-pointer hover:text-blue-600 font-semibold text-xl"
        >
          {getFullName(r.father)}
        </span>
      ),
    },
    {
      title: "👩 الأم",
      render: (r: IFamily) => (
        <span
          onClick={() => navigate(`/families/${r._id}/mother`)}
          className="cursor-pointer hover:text-pink-600 font-semibold text-xl"
        >
          {getFullName(r.mother)}
        </span>
      ),
    },
    {
      title: "عدد الأبناء",
      align: "center",
      render: (r: IFamily) => {
        const count = r.children?.length || 0;
        return (
          <Tag
            color={count === 0 ? "red" : count < 3 ? "orange" : "green"}
            className="px-4 py-1 font-semibold"
          >
            {count}
          </Tag>
        );
      },
    },
    {
      title: "⚙️ الإجراءات",
      align: "center",
      render: (r: IFamily) => (
        <Space>
          <Tooltip title="تعديل">
            <Button
              size="middle"
              className="bg-yellow-100 border-yellow-300 hover:bg-yellow-200"
              icon={<EditOutlined className="text-yellow-700" />}
              onClick={() => edit(r)}
            />
          </Tooltip>
          <Popconfirm
            placement="bottomRight"
            title={`Delete selected family`}
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => r._id && remove(r._id.toString())}
            disabled={isLoading}
          >
            <Tooltip title="حذف">
              <Button
                size="middle"
                danger
                className="bg-red-100 border-red-300 hover:bg-red-200"
                icon={<DeleteOutlined className="text-red-700" />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider direction={rtl ? "rtl" : "ltr"}>
      <motion.div
        className="p-4 sm:p-6 min-h-screen"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <Card
          title={
            <span className="text-lg sm:text-xl font-bold">
              📋 قائمة العائلات
            </span>
          }
          bordered={false}
          className="shadow-md rounded-2xl"
          headStyle={{
            position: "sticky",
            top: 64, // ← adjust to your Navbar height
            zIndex: 20,
            background: "#fff", // ensure it covers content under it
          }}
          extra={
            <Space
              wrap
              className="flex flex-wrap gap-2 justify-end w-full sm:w-auto"
            >
              <Search
                placeholder="ابحث بالاسم أو رقم الدفتر"
                allowClear
                enterButton={<SearchOutlined />}
                onSearch={(e) => setQuery(e)}
                className="w-full sm:w-60"
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={add}>
                إضافة
              </Button>
              <Button
                icon={<FileExcelOutlined />}
                onClick={exportExcel}
                style={{ backgroundColor: "#10793F", color: "white" }}
              >
                تصدير
              </Button>
              <Button icon={<SyncOutlined />} onClick={() => refetch()} />
              <Button icon={<SwapOutlined />} onClick={() => setRtl(!rtl)} />
            </Space>
          }
        >
          <div className="overflow-x-auto">
            <Table<IFamily>
              rowKey="_id"
              dataSource={filtered}
              loading={isLoading || isRefetching}
              pagination={{
                current: page,
                pageSize,
                total,
                onChange: (newPage, newPageSize) => {
                  if (newPageSize !== pageSize) {
                    // Reset to first page when size changes
                    setPage(1);
                    setPageSize(newPageSize);
                  } else {
                    setPage(newPage);
                  }
                },
                showSizeChanger: true,
                showQuickJumper: !isMobile,
                position: ["bottomRight"],
                size: "small",
                hideOnSinglePage: false,
                showLessItems: true,
                showTotal: (total, range) =>
                  `${range[0]}–${range[1]} من ${total} سجل`,
                itemRender: (current, type, original) => {
                  if (type === "prev")
                    return (
                      <Tooltip title="الصفحة السابقة">
                        <a>
                          <RightOutlined />
                        </a>
                      </Tooltip>
                    );
                  if (type === "next")
                    return (
                      <Tooltip title="الصفحة التالية">
                        <a>
                          <LeftOutlined />
                        </a>
                      </Tooltip>
                    );
                  return original;
                },
              }}
              bordered
              size="middle"
              scroll={{ x: "max-content" }}
              locale={{
                emptyText: (
                  <Empty
                    description="لا توجد بيانات متاحة حاليًا"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
              columns={columns}
              rowClassName={(_, index) =>
                index % 2 === 0
                  ? "bg-gray-50 hover:bg-gray-100"
                  : "bg-white hover:bg-gray-100"
              }
            />
          </div>
        </Card>

        {modalForm.visible && (
          <FamilyForm
            open={modalForm.visible}
            selected={modalForm?.selected}
            onClose={() => {
              reset();
              refetch();
            }}
          />
        )}
      </motion.div>
    </ConfigProvider>
  );
}
